import { useEffect, useRef, useState } from "react";

/**
 * Keeps keyboard focus on the password visibility toggle after it is activated.
 *
 * The antd input wrapper focuses the input on any click inside it, including a keyboard-triggered
 * click on the toggle, so a keyboard user would land back in the input and never hear the toggle's
 * new "Show password" / "Hide password" name. A mouse press never focuses the toggle (antd prevents
 * it on mousedown), so mouse clicks keep antd's default of focusing the input.
 *
 * @return a ref for the toggle button and a controlled `visibilityToggle` config for `Input.Password`
 */
export function usePasswordToggleFocus() {
  const [visible, setVisible] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const restoreToggleFocus = useRef(false);

  // Runs after the click has finished bubbling, so it wins over the wrapper's input focus.
  useEffect(() => {
    if (!restoreToggleFocus.current) {
      return;
    }

    restoreToggleFocus.current = false;
    toggleRef.current?.focus();
  }, [visible]);

  const onVisibleChange = (nextVisible: boolean) => {
    restoreToggleFocus.current = document.activeElement === toggleRef.current;
    setVisible(nextVisible);
  };

  return { toggleRef, visibilityToggle: { visible, onVisibleChange } };
}
